import React, { Fragment, useCallback } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import {
  useCreateEvaluationMutation,
  useEvaluationFormQuery,
  useUpdateCandidateMutation,
} from "../../graphql/generated/graphql";
import StarRating from "../StarRating/StarRating";
import LoadingButton from "../Button/LoadingButton";

interface ModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  candidateId: number;
  jobId: number;
  criteriaId: number;
  scorecardTitle: string;
  scorecardId: number;
  onSubmit: (
    evaluationScorecards: Array<{
      scorecardId: number;
      scorecardCriterias: {
        create: Array<{
          criteriaId: number;
          point: number;
        }>;
      };
    }>,
    point: number,
    comment: string,
    scorecards: Record<string, any>,
    scorecardTitle: string,
    jobId: number,
    candidateId: number
  ) => void;
}

const validationSchema = Yup.object().shape({
  point: Yup.number()
    .required("امتیاز الزامی است")
    .min(1, "امتیاز باید حداقل 1 باشد")
    .max(5, "امتیاز باید حداکثر 5 باشد"),
  comment: Yup.string().required("ثبت نظر الزامی است"),
});

interface FormValues {
  jobId: number | null;
  scorecardId: number;
  questionnaireIds: number[];
  comment: string;
  point: number;
  scorecards: Record<string, any>;
  criteriaId: number | null;
  candidateId: number;
  scorecardTitle: string;
}

export default function EvaluateModal({
  open,
  setOpen,
  candidateId,
  jobId,
  criteriaId,
  scorecardTitle,
  scorecardId,
  onSubmit,
}: ModalProps) {
  const [createEvaluationMutation, { loading }] = useCreateEvaluationMutation();
  const [updateCandidateMutation] = useUpdateCandidateMutation();

  const { data, error } = useEvaluationFormQuery({
    variables: {
      scorecardIds: [scorecardId],
      questionnaireIds: [],
    },
  });

  const handleCancel = useCallback(() => setOpen(false), [setOpen]);

  const handleSubmit = async (values: FormValues) => {
    try {
      const { point, comment, scorecards, scorecardTitle } = values;

      if (!jobId) {
        throw new Error("اطلاعات مورد نیاز کامل نیست");
      }

      const evaluationScorecards = Object.entries(scorecards).map(
        ([scorecardId, criteria]) => ({
          scorecardId: parseInt(scorecardId, 10),
          scorecardCriterias: {
            create: Object.entries(criteria as Record<string, any>).map(
              ([criteriaId, data]) => ({
                criteriaId: parseInt(criteriaId, 10),
                point: Math.max(1, Math.min(5, data.point)),
                ...data,
              })
            ),
          },
        })
      );

      await updateCandidateMutation({
        variables: {
          input: {
            id: candidateId,
            patch: {
              evaluations: {
                create: [
                  {
                    point: Math.max(1, Math.min(5, point)),
                    evaluationScorecards: {
                      create: evaluationScorecards.map((scorecard) => ({
                        scorecardId: scorecard.scorecardId,
                        scorecardCriterias: scorecard.scorecardCriterias,
                      })),
                    },
                  },
                ],
              },
            },
          },
        },
      });

      await createEvaluationMutation({
        variables: {
          input: {
            evaluation: {
              candidateId,
              jobId,
              point: Math.max(0, Math.min(5, point)),
              comment: comment || null,
              evaluationScorecards: {
                create: evaluationScorecards.map((scorecard) => ({
                  scorecardId: scorecard.scorecardId,
                  scorecardCriterias: scorecard.scorecardCriterias,
                })),
              },
            },
          },
        },
      });

      onSubmit(
        evaluationScorecards,
        point,
        comment,
        scorecards,
        scorecardTitle,
        jobId,
        candidateId
      );
      setOpen(false);
      toast.success("ارزیابی با موفقیت انجام شد");
    } catch (error) {
      console.error("خطا در ارسال ارزیابی:", error);
      toast.error("خطا در ثبت ارزیابی");
    }
  };

  const initialValues: FormValues = {
    jobId: jobId || null,
    scorecardId: scorecardId || 0,
    questionnaireIds: [],
    scorecards:
      data?.scorecards?.nodes.reduce((acc, scorecard) => {
        acc[scorecard.id] = {};
        scorecard.criterias.nodes.forEach((criteria) => {
          acc[scorecard.id][criteria.id] = { point: 1 };
        });
        return acc;
      }, {} as Record<string, any>) || {},
    criteriaId: criteriaId || null,
    candidateId: candidateId,
    scorecardTitle: scorecardTitle || "",
    comment: "",
    point: 1,
  };

  if (error) {
    return <div>خطا در دریافت اطلاعات</div>;
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-10 overflow-y-auto"
        onClose={handleCancel}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>
        <div className="flex min-h-screen items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="relative bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                {scorecardTitle}
              </h2>
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ setFieldValue, values, isSubmitting }) => (
                  <Form>
                    <div className="mb-4">
                      <ErrorMessage
                        name="scorecardId"
                        component="div"
                        className="text-red-600 text-sm mt-1"
                      />
                    </div>
                    <div className="mb-4 flex w-full">
                      <label
                        htmlFor="point"
                        className="block text-md font-medium text-gray-700 w-1/2"
                      >
                        ارزیابی:
                      </label>
                      <StarRating
                        initialRate={values?.point}
                        starClick={(rate) => setFieldValue("point", rate)}
                        className="w-3/4"
                      />
                      <ErrorMessage
                        name="point"
                        component="div"
                        className="text-red-600 text-sm mt-1"
                      />
                    </div>
                    <div className="mb-4">
                      <label
                        htmlFor="comment"
                        className="block text-md font-medium text-gray-700"
                      >
                        نظر:
                      </label>
                      <Field
                        as="textarea"
                        id="comment"
                        name="comment"
                        className="p-2 mt-2 block w-full border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        rows={2}
                      />
                      <ErrorMessage
                        name="comment"
                        component="div"
                        className="text-red-600 text-sm mt-1"
                      />
                    </div>
                    <div className="flex justify-end gap-x-4">
                      <button
                        type="button"
                        className="text-white w-20 px-4 py-2 rounded-md border-slate-300 shadow font-semibold bg-[#b23226] bg-opacity-80"
                        onClick={handleCancel}
                      >
                        انصراف
                      </button>
                      <button
                        type="submit"
                        className="text-white w-20 px-4 py-2 rounded-md border-slate-300 shadow font-semibold bg-primary"
                        disabled={isSubmitting}
                      >
                        {loading ? <LoadingButton /> : "ثبت"}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
